// sequenceGenerator.js
// Generatore automatico di sequenze staffetta "zig-zag dal basso" per NxM (max 8)

/**
 * Genera una sequenza staffetta "zig-zag dal basso" per NxM
 * - Nessun tiratore fa due assalti di fila su nessun lato (se possibile)
 * - Gli indici vengono compilati dal basso (dal più alto al più basso)
 * - Evita accoppiamenti duplicati (stesso tiratore vs stesso avversario)
 * @param {number} nRed - Numero partecipanti squadra rossa
 * @param {number} nGreen - Numero partecipanti squadra verde
 * @returns {Array<[number,number]>} sequenza di accoppiamenti [idxRosso, idxVerde]
 */
export function generateZigZagBottomUpSequence(nRed, nGreen) {
  const sequence = [];
  if (nRed < 1 || nGreen < 1) return sequence;
  
  // Caso speciale: 2x2 tiratori (caso comune che richiede attenzione particolare)
  if (nRed === 2 && nGreen === 2) {
    // Genera esplicitamente tutti gli accoppiamenti possibili senza duplicazioni
    return [[1, 1], [0, 0], [1, 0], [0, 1]]; // Ordine ottimale per staffetta
  }
  
  // Per tutti gli altri casi, usa un algoritmo che evita duplicazioni
  const usedPairs = new Set(); // Tiene traccia delle coppie già generate
  const maxAssalti = nRed * nGreen; // Numero massimo teorico di assalti
  
  // Crea la lista degli indici dal basso
  const redOrder = Array.from({length: nRed}, (_, i) => nRed - 1 - i);
  const greenOrder = Array.from({length: nGreen}, (_, i) => nGreen - 1 - i);

  // Zig-zag con controllo duplicazioni
  let redIdx = 0, greenIdx = 0, lastRed = null, lastGreen = null;
  let tentativi = 0; // Evita loop infiniti
  
  while (sequence.length < maxAssalti && tentativi < maxAssalti * 3) {
    // Trova il prossimo rosso che non sia uguale al precedente
    let nextRed = redOrder[redIdx % nRed];
    if (nextRed === lastRed) redIdx++;
    nextRed = redOrder[redIdx % nRed];
    
    // Trova il prossimo verde che non sia uguale al precedente
    let nextGreen = greenOrder[greenIdx % nGreen];
    if (nextGreen === lastGreen) greenIdx++;
    nextGreen = greenOrder[greenIdx % nGreen];
    
    // Verifica che questa coppia non sia già stata usata
    const pairKey = `${nextRed},${nextGreen}`;
    if (!usedPairs.has(pairKey)) {
      sequence.push([nextRed, nextGreen]);
      usedPairs.add(pairKey);
      lastRed = nextRed;
      lastGreen = nextGreen;
    } else {
      // Se la coppia è già stata usata, prova con la prossima
      redIdx++;
      greenIdx++;
    }
    
    tentativi++;
  }
  
  // Se non abbiamo generato tutti gli accoppiamenti possibili con l'algoritmo principale,
  // completa con una generazione sistematica delle coppie mancanti
  if (sequence.length < maxAssalti) {
    for (let r = 0; r < nRed; r++) {
      for (let g = 0; g < nGreen; g++) {
        const key = `${redOrder[r]},${greenOrder[g]}`;
        if (!usedPairs.has(key)) {
          sequence.push([redOrder[r], greenOrder[g]]);
          usedPairs.add(key);
        }
      }
    }
  }
  
  return sequence;
}

/**
 * Utility per ottenere la sequenza staffetta dal basso per qualsiasi NxM (fino a 8)
 * @param {number} nRed
 * @param {number} nGreen
 * @returns {Array<[number,number]>}
 */
export function getBottomUpFederationSequence(nRed, nGreen) {
  if (nRed < 1 || nGreen < 1 || nRed > 8 || nGreen > 8) return [];
  return generateZigZagBottomUpSequence(nRed, nGreen);
}

/**
 * Genera una sequenza di incontri tra squadre con pattern zig-zag
 * per evitare che una squadra giochi due incontri consecutivi
 * e alternando i lati (rosso/verde) per ogni squadra
 * @param {number} numTeams - Numero di squadre
 * @returns {Array<[number,number,boolean]>} sequenza di incontri [teamA, teamB, invertLati]
 */
export function generateZigZagTeamMatches(numTeams) {
  // Se abbiamo meno di 2 squadre, non ci sono incontri
  if (numTeams < 2) return [];
  
  // Array per tenere traccia delle squadre che hanno giocato l'ultimo incontro
  let lastPlayed = new Set();
  // Array per memorizzare gli incontri generati
  const matches = [];
  // Tutte le possibili combinazioni di incontri
  const allPossibleMatches = [];
  
  // Per ogni squadra, teniamo traccia di quante volte è stata a sinistra (rosso)
  const leftSideCount = Array(numTeams).fill(0);
  
  // Genera tutte le possibili combinazioni (i,j) dove i < j
  for (let i = 0; i < numTeams; i++) {
    for (let j = i + 1; j < numTeams; j++) {
      allPossibleMatches.push([i, j]);
    }
  }
  
  // Finché ci sono incontri da generare
  while (allPossibleMatches.length > 0) {
    // Trova il prossimo incontro che non coinvolge squadre che hanno appena giocato
    let nextMatchIndex = -1;
    
    for (let i = 0; i < allPossibleMatches.length; i++) {
      const [teamA, teamB] = allPossibleMatches[i];
      // Se nessuna delle due squadre ha giocato l'ultimo incontro, seleziona questo
      if (!lastPlayed.has(teamA) && !lastPlayed.has(teamB)) {
        nextMatchIndex = i;
        break;
      }
    }
    
    // Se non troviamo un incontro ideale, prendiamo il primo disponibile
    if (nextMatchIndex === -1) nextMatchIndex = 0;
    
    // Aggiungi l'incontro alla sequenza e rimuovilo dalle possibilità
    let [teamA, teamB] = allPossibleMatches.splice(nextMatchIndex, 1)[0];
    
    // Decidi se invertire i lati in base a quante volte ogni squadra è stata a sinistra
    let invertLati = false;
    if (leftSideCount[teamA] > leftSideCount[teamB]) {
      // Se teamA è stata più volte a sinistra, invertiamo
      invertLati = true;
      [teamA, teamB] = [teamB, teamA]; // Scambia le squadre
    } else if (leftSideCount[teamA] === leftSideCount[teamB]) {
      // Se pari, decidi casualmente
      invertLati = Math.random() < 0.5;
      if (invertLati) {
        [teamA, teamB] = [teamB, teamA]; // Scambia le squadre
      }
    }
    
    // Aggiorna il contatore per la squadra a sinistra
    leftSideCount[teamA]++;
    
    // Aggiungi l'incontro con l'indicazione se i lati sono invertiti
    matches.push([teamA, teamB, invertLati]);
    
    // Aggiorna le squadre che hanno appena giocato
    lastPlayed = new Set([teamA, teamB]);
    
    // Se rimangono solo pochi incontri e tutte le squadre hanno già giocato,
    // resettiamo lastPlayed per evitare stalli
    if (allPossibleMatches.length <= numTeams/2 && lastPlayed.size >= numTeams-2) {
      lastPlayed = new Set();
    }
  }
  
  return matches;
}
